// ── services/api/client.ts ────────────────────────────────────────────────────
// Typed fetch wrapper. All API calls go through this.
//   - Reads AUTH_TOKEN from localStorage (set by useAuthStore on login)
//   - On 401 → tries token refresh, then retries once
//   - On network failure → throws ApiError with code 'NETWORK_ERROR'
// ────────────────────────────────────────────────────────────────────────────

export interface ApiError {
  code:    string;
  message: string;
  status:  number;
  errors?: Record<string, string[]>;
}

export class ApiException extends Error {
  constructor(
    public readonly status: number,
    public readonly code:   string,
    public readonly errors?: Record<string, string[]>
  ) {
    super(code);
    this.name = 'ApiException';
  }
}

const BASE_URL  = import.meta.env['VITE_API_URL'] ?? '';       // '' = same origin (proxied)
const API_PREFIX = '/api/v1';

function getToken(): string | null {
  try {
    const raw = localStorage.getItem('foi-auth');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { token?: string; state?: { token?: string } };
    return parsed.state?.token ?? parsed.token ?? null;
  } catch {
    return null;
  }
}

function setToken(token: string) {
  try {
    const raw = localStorage.getItem('foi-auth');
    const existing = raw ? (JSON.parse(raw) as { state?: Record<string, unknown> }) : {};
    if (existing.state) {
      existing.state.token = token;
      localStorage.setItem('foi-auth', JSON.stringify(existing));
    } else {
      localStorage.setItem('foi-auth', JSON.stringify({ ...existing, token }));
    }
  } catch { /* ignore */ }
}

// ── Single-flight token refresh ────────────────────────────────────────────────
// The refresh token is single-use (the backend revokes it the instant it's
// redeemed and issues a new one). If several requests race to a 401 at once —
// exactly what happens when a backgrounded PWA resumes and multiple screens
// refetch simultaneously — each one independently calling /auth/refresh means
// only the FIRST redemption succeeds; every other one gets "refresh token
// invalid" for a token that was perfectly valid a moment ago, and would wrongly
// force a full logout. Coalescing concurrent 401s onto one shared refresh call
// eliminates that race entirely.
let refreshPromise: Promise<{ token: string; refreshToken?: string } | null> | null = null;

function refreshAccessToken(): Promise<{ token: string; refreshToken?: string } | null> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const raw = localStorage.getItem('foi-auth');
      const parsed = raw ? (JSON.parse(raw) as { refreshToken?: string; state?: { refreshToken?: string } }) : null;
      const refreshToken = parsed?.state?.refreshToken ?? parsed?.refreshToken ?? null;
      if (!refreshToken) return null;

      const refreshRes = await fetch(`${BASE_URL}${API_PREFIX}/auth/refresh`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ refreshToken }),
      });
      if (!refreshRes.ok) return null;

      const data = await refreshRes.json() as { token: string; refreshToken?: string };
      setToken(data.token);
      // Persist rotated refresh token when provided
      if (data.refreshToken) {
        try {
          const rawStore = localStorage.getItem('foi-auth');
          if (rawStore) {
            const store = JSON.parse(rawStore) as { state?: Record<string, unknown>; refreshToken?: string };
            if (store.state) {
              store.state.refreshToken = data.refreshToken;
              store.state.token = data.token;
            } else {
              store.refreshToken = data.refreshToken;
            }
            localStorage.setItem('foi-auth', JSON.stringify(store));
          }
        } catch { /* ignore persist errors */ }
      }
      return data;
    } catch {
      return null;
    } finally {
      // Cleared as soon as this cycle settles — the NEXT access-token expiry
      // (15 minutes later) should trigger its own fresh refresh, not reuse this.
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// ── Core request ──────────────────────────────────────────────────────────────

async function request<T>(
  path:    string,
  options: RequestInit = {},
  _retry  = false,
): Promise<T> {
  const token = getToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers as Record<string, string> ?? {}),
  };

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${API_PREFIX}${path}`, {
      ...options,
      headers,
    });
  } catch {
    throw new ApiException(0, 'NETWORK_ERROR');
  }

  // Token refresh on 401 (retry once, except on login/signup endpoints).
  // Concurrent 401s from other in-flight requests share the SAME refresh —
  // see refreshAccessToken() — rather than each redeeming the single-use
  // refresh token independently and racing each other.
  if (
    response.status === 401 &&
    !_retry &&
    !path.startsWith('/auth/login') &&
    !path.startsWith('/auth/signup')
  ) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      return request<T>(path, options, true);
    }

    // Refresh failed — clear auth and reload
    localStorage.removeItem('foi-auth');
    window.location.href = '/login';
    throw new ApiException(401, 'UNAUTHORIZED');
  }

  if (!response.ok) {
    let err: ApiError = { code: 'UNKNOWN_ERROR', message: 'Something went wrong', status: response.status };
    try {
      err = await response.json() as ApiError;
    } catch { /* use default */ }
    throw new ApiException(response.status, err.code, err.errors);
  }

  // 204 No Content
  if (response.status === 204) return undefined as T;

  return response.json() as Promise<T>;
}

// ── Convenience methods ───────────────────────────────────────────────────────

export const api = {
  get:    <T>(path: string, opts?: RequestInit) =>
    request<T>(path, { ...opts, method: 'GET' }),

  post:   <T>(path: string, body?: unknown, opts?: RequestInit) =>
    // Always send `{}` when body is omitted — Fastify rejects empty body + application/json
    request<T>(path, { ...opts, method: 'POST', body: JSON.stringify(body ?? {}) }),

  patch:  <T>(path: string, body?: unknown, opts?: RequestInit) =>
    request<T>(path, { ...opts, method: 'PATCH', body: JSON.stringify(body ?? {}) }),

  put:    <T>(path: string, body?: unknown, opts?: RequestInit) =>
    request<T>(path, { ...opts, method: 'PUT', body: JSON.stringify(body ?? {}) }),

  delete: <T>(path: string, opts?: RequestInit) =>
    // Always send `{}` — Fastify rejects empty body + application/json
    request<T>(path, { ...opts, method: 'DELETE', body: JSON.stringify({}) }),
};
